import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Sparkles, CheckCircle, XCircle, SlidersHorizontal, Code2, Bot, Share2, X, Loader } from 'lucide-react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import TradingChart from '../../components/charts/TradingChart';
import BacktestReplayControls from '../../components/BacktestReplayControls';
import { formatPrice } from '../../utils/formatters';
import ShareToForumModal from '../../components/ShareToForumModal';
import BacktestShareCard from '../../components/BacktestShareCard';
import { captureAndShare } from '../../utils/shareImage';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function BacktestSection({ onBack }) {
  const { colors } = useTheme();
  const location = useLocation();
    const [stocks, setStocks] = useState([]);
    const [symbol, setSymbol] = useState('');
    const [startDate, setStartDate] = useState(() => {
      const d = new Date(); d.setFullYear(d.getFullYear() - 1);
      return d.toISOString().split('T')[0];
    });
    const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
    const [capital, setCapital] = useState(100000);
    const [quantity, setQuantity] = useState(10);
    const [entryLogic, setEntryLogic] = useState('ALL');
    const [selectedConditions, setSelectedConditions] = useState([]);
    const [slPct, setSlPct] = useState(5);
    const [tpPct, setTpPct] = useState(10);
    const [signalExit, setSignalExit] = useState(true);
    const [runName, setRunName] = useState('');

    // New production-grade fields
    const [slippagePct, setSlippagePct] = useState(0.1);
    const [commissionPerOrder, setCommissionPerOrder] = useState(20);
    const [trailingSl, setTrailingSl] = useState(0);
    const [sizingMode, setSizingMode] = useState('fixed');
    const [riskPerTrade, setRiskPerTrade] = useState(2);

    // 3-mode support
    const [strategyMode, setStrategyMode] = useState('visual'); // 'visual' | 'code' | 'ai'
    const [strategyCode, setStrategyCode] = useState('');
    const [aiPrompt, setAiPrompt] = useState('');
    const [aiGenerating, setAiGenerating] = useState(false);
    const [codeValid, setCodeValid] = useState(null); // null | {valid, error, has_entry, has_exit}
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [activeCategory, setActiveCategory] = useState('all');
    const [dragOverBuilder, setDragOverBuilder] = useState(false);
    const draggedCondId = useRef(null);
    const [showCodeGuide, setShowCodeGuide] = useState(false);
    const [exitConditions, setExitConditions] = useState([]);
    const [exitLogic, setExitLogic] = useState('ANY');
    const [dragOverExitBuilder, setDragOverExitBuilder] = useState(false);
    const draggedExitCondId = useRef(null);
    const [availableTemplates, setAvailableTemplates] = useState([]);
    const [showTemplates, setShowTemplates] = useState(false);

    // Portfolio mode
    const [backtestMode, setBacktestMode] = useState('single'); // 'single' | 'portfolio'
    const [portfolioSymbols, setPortfolioSymbols] = useState([]);
    const [portfolioSymbolInput, setPortfolioSymbolInput] = useState('');
    const [maxPositions, setMaxPositions] = useState(5);
    const [portfolioResults, setPortfolioResults] = useState(null);
    const [portfolioLoading, setPortfolioLoading] = useState(false);
    const [expandedStock, setExpandedStock] = useState(null);
    const [watchlists, setWatchlists] = useState([]);

    const [availableConditions, setAvailableConditions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState(null);
    const [error, setError] = useState('');
    const [history, setHistory] = useState([]);
    const [view, setView] = useState('config'); // 'config' | 'results' | 'history' | 'from-strategy'
    const [shareOpen, setShareOpen] = useState(false);
    const [socialOpen, setSocialOpen] = useState(false);
    const [capturing, setCapturing] = useState(false);
    const shareCardRef = useRef(null);

    // From-strategy view state
    const [algoStrategies, setAlgoStrategies] = useState([]);
    const [fsStrategyId, setFsStrategyId] = useState('');
    const [fsStrategyData, setFsStrategyData] = useState(null);
    const [fsSymbol, setFsSymbol] = useState('');
    const [fsStartDate, setFsStartDate] = useState(() => {
      const d = new Date(); d.setFullYear(d.getFullYear() - 1); return d.toISOString().split('T')[0];
    });
    const [fsEndDate, setFsEndDate] = useState(() => new Date().toISOString().split('T')[0]);
    const [fsCapital, setFsCapital] = useState(100000);
    const [fsQty, setFsQty] = useState(10);
    const [fsLoading, setFsLoading] = useState(false);
    const [fsResult, setFsResult] = useState(null);
    const [fsError, setFsError] = useState('');
    const [highlightedTradeIndex, setHighlightedTradeIndex] = useState(null);
    const [replayUpToDate, setReplayUpToDate] = useState(null);
    const [candleDates, setCandleDates] = useState([]);

    // Reset replay state when leaving results view
    useEffect(() => {
      if (view !== 'results') {
        setHighlightedTradeIndex(null);
      }
    }, [view]);

    useEffect(() => {
      axios.get(`${API_URL}/stocks`).then(r => setStocks(r.data || [])).catch(() => {});
      axios.get(`${API_URL}/backtest/conditions`).then(r => setAvailableConditions(r.data.conditions || [])).catch(() => {});
      axios.get(`${API_URL}/backtest/templates`).then(r => setAvailableTemplates(r.data.templates || [])).catch(() => {});
      axios.get(`${API_URL}/backtest/results?limit=20`).then(r => setHistory(r.data || [])).catch(() => {});
      axios.get(`${API_URL}/watchlists`).then(r => setWatchlists(r.data || [])).catch(() => {});
    }, []);

    // Auto-load a specific result when navigated from Algo Flows backtest panel
    useEffect(() => {
      const resultId = location?.state?.loadResultId;
      if (!resultId) return;
      axios.get(`${API_URL}/backtest/result/${resultId}`)
        .then(r => { setResults(r.data); setView('results'); })
        .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load template when switching to code mode for first time
    useEffect(() => {
      if (strategyMode === 'code' && !strategyCode) {
        axios.get(`${API_URL}/backtest/strategy-template`).then(r => {
          setStrategyCode(r.data.template || '');
        }).catch(() => {});
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [strategyMode]);

    const handleValidateCode = async () => {
      try {
        const res = await axios.post(`${API_URL}/backtest/validate-code`, { code: strategyCode });
        setCodeValid(res.data);
      } catch (e) {
        setCodeValid({ valid: false, error: e.response?.data?.detail || 'Validation failed' });
      }
    };

    const handleAiGenerate = async () => {
      if (!aiPrompt.trim()) { setError('Describe your strategy first'); return; }
      setAiGenerating(true);
      setError('');
      try {
        const res = await axios.post(`${API_URL}/backtest/generate-strategy`, {
          prompt: aiPrompt,
          symbol: symbol || '',
        });
        setStrategyCode(res.data.code || '');
        setCodeValid({ valid: res.data.valid, error: res.data.validation_error, has_entry: res.data.has_entry, has_exit: res.data.has_exit });
        if (res.data.valid) {
          setStrategyMode('code'); // Switch to code view to show generated code
        }
      } catch (e) {
        const detail = e.response?.data?.detail || '';
        const status = e.response?.status;
        if (status === 429 || /quota|limit|exceed|rate/i.test(detail)) {
          setError('AI quota reached — try again in a few minutes');
        } else {
          setError(detail || 'AI generation failed');
        }
      } finally {
        setAiGenerating(false);
      }
    };

    const handleRun = async () => {
      if (!symbol) { setError('Select a stock symbol'); return; }
      if (strategyMode === 'visual' && selectedConditions.length === 0) { setError('Select at least one entry condition'); return; }
      if (strategyMode === 'code' && !strategyCode.trim()) { setError('Write strategy code first'); return; }
      setError('');
      setLoading(true);
      try {
        const res = await axios.post(`${API_URL}/backtest/run`, {
          symbol,
          start_date: startDate,
          end_date: endDate,
          capital: parseFloat(capital),
          quantity: parseInt(quantity),
          conditions: strategyMode === 'visual' ? selectedConditions : [],
          entry_logic: entryLogic,
          exit_conditions: strategyMode === 'visual' ? exitConditions : [],
          exit_logic: exitLogic,
          stop_loss_pct: parseFloat(slPct),
          target_pct: parseFloat(tpPct),
          signal_exit: signalExit,
          name: runName || `${symbol.replace('.NS','')} ${startDate}?${endDate}`,
          save: true,
          mode: strategyMode === 'ai' ? 'ai' : strategyMode,
          strategy_code: (strategyMode === 'code' || strategyMode === 'ai') ? strategyCode : '',
          slippage_pct: parseFloat(slippagePct),
          commission_per_order: parseFloat(commissionPerOrder),
          trailing_sl_pct: parseFloat(trailingSl),
          sizing_mode: sizingMode,
          risk_per_trade_pct: parseFloat(riskPerTrade),
        });
        setResults(res.data);
        setView('results');
        axios.get(`${API_URL}/backtest/results?limit=20`).then(r => setHistory(r.data || [])).catch(() => {});
      } catch (e) {
        setError(e.response?.data?.detail || 'Backtest failed');
      } finally {
        setLoading(false);
      }
    };

    const handlePortfolioRun = async () => {
      if (portfolioSymbols.length === 0) { setError('Add at least one stock'); return; }
      if (selectedConditions.length === 0) { setError('Select at least one entry condition'); return; }
      setError(''); setPortfolioLoading(true);
      try {
        const res = await axios.post(`${API_URL}/backtest/run-portfolio`, {
          symbols: portfolioSymbols,
          start_date: startDate,
          end_date: endDate,
          capital: parseFloat(capital),
          max_positions: parseInt(maxPositions),
          conditions: selectedConditions,
          entry_logic: entryLogic,
          exit_conditions: exitConditions,
          exit_logic: exitLogic,
          stop_loss_pct: parseFloat(slPct),
          target_pct: parseFloat(tpPct),
          signal_exit: signalExit,
          slippage_pct: parseFloat(slippagePct),
          commission_per_order: parseFloat(commissionPerOrder),
          trailing_sl_pct: parseFloat(trailingSl),
        });
        setPortfolioResults(res.data);
        setView('portfolio-results');
      } catch (e) {
        setError(e.response?.data?.detail || 'Portfolio backtest failed');
      } finally {
        setPortfolioLoading(false);
      }
    };

    const handleLoadResult = async (id) => {
      try {
        const res = await axios.get(`${API_URL}/backtest/result/${id}`);
        setResults(res.data);
        setView('results');
      } catch { setError('Failed to load result'); }
    };

    // Load algo flow strategies when from-strategy tab is first opened
    useEffect(() => {
      if (view !== 'from-strategy') return;
      axios.get(`${API_URL}/algo-flows/dashboard`).then(r => {
        setAlgoStrategies(r.data?.strategies || []);
      }).catch(() => {});
    }, [view]);

    // When strategy selection changes, load full strategy data and pre-fill capital
    useEffect(() => {
      if (!fsStrategyId) { setFsStrategyData(null); return; }
      axios.get(`${API_URL}/algo-flows/strategies/${fsStrategyId}`).then(r => {
        setFsStrategyData(r.data);
        setFsCapital(r.data.capital_allocated || 100000);
        if (r.data.stocks?.length > 0) setFsSymbol(r.data.stocks[0]);
      }).catch(() => {});
    }, [fsStrategyId]);

    const handleRunFromStrategy = async () => {
      if (!fsStrategyId) { setFsError('Select a strategy'); return; }
      if (!fsSymbol) { setFsError('Select a stock symbol'); return; }
      setFsError('');
      setFsLoading(true);
      setFsResult(null);
      try {
        const res = await axios.post(`${API_URL}/backtest/run-from-strategy/${fsStrategyId}`, {
          symbol: fsSymbol,
          start_date: fsStartDate,
          end_date: fsEndDate,
          capital: parseFloat(fsCapital),
          quantity: parseInt(fsQty),
        });
        setFsResult(res.data);
        axios.get(`${API_URL}/backtest/results?limit=20`).then(r => setHistory(r.data || [])).catch(() => {});
      } catch (e) {
        setFsError(e.response?.data?.detail || 'Backtest failed');
      } finally {
        setFsLoading(false);
      }
    };

    const pct = (v) => v != null ? `${v >= 0 ? '+' : ''}${parseFloat(v).toFixed(2)}%` : '…';

    const condDir = (c) => {
      const s = ((c.id || '') + ' ' + (c.label || '')).toLowerCase();
      if (/oversold|bullish|buy|above|spike|strong/.test(s)) return 'bullish';
      if (/overbought|bearish|sell/.test(s)) return 'bearish';
      return 'neutral';
    };
    const dirColor = (c) => condDir(c) === 'bullish' ? '#16C784' : condDir(c) === 'bearish' ? '#ef4444' : '#f59e0b';

    const cs = { background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, borderWidth: '1px', borderColor: colors.accentBorder };
    const inputStyle = { backgroundColor: colors.inputBg, color: colors.primaryText, borderWidth: '1px', borderColor: colors.border, outline: 'none' };

    // Monthly returns heatmap helper
    const renderMonthlyReturns = (monthlyReturns) => {
      if (!monthlyReturns || monthlyReturns.length === 0) return null;
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      const years = [...new Set(monthlyReturns.map(m => m.year))].sort();
      const lookup = {};
      monthlyReturns.forEach(m => { lookup[`${m.year}-${m.month}`] = m; });

      return (
        <div className="rounded-lg p-1.5 overflow-x-auto" style={cs}>
          <p className="text-[13px] font-semibold mb-2" style={{ color: colors.secondaryText }}>Monthly Returns Heatmap</p>
          <table className="w-full text-[13px]">
            <thead>
              <tr>
                <th className="text-left py-1 px-1.5" style={{ color: colors.secondaryText }}>Year</th>
                {months.map(m => <th key={m} className="text-center py-1 px-0.5" style={{ color: colors.secondaryText }}>{m}</th>)}
              </tr>
            </thead>
            <tbody>
              {years.map(year => (
                <tr key={year}>
                  <td className="py-1 px-1.5 font-semibold" style={{ color: colors.primaryText }}>{year}</td>
                  {months.map((_, mi) => {
                    const d = lookup[`${year}-${mi + 1}`];
                    const pnl = d ? d.pnl : null;
                    const bg = pnl === null ? 'transparent' : pnl > 0 ? `rgba(34,197,94,${Math.min(Math.abs(pnl) / 5000, 0.7)})` : `rgba(239,68,68,${Math.min(Math.abs(pnl) / 5000, 0.7)})`;
                    return (
                      <td key={mi} className="text-center py-1 px-0.5 rounded" style={{ backgroundColor: bg, color: pnl === null ? colors.border : pnl >= 0 ? '#16C784' : '#ef4444' }}>
                        {pnl !== null ? `${pnl >= 0 ? '+' : ''}${(pnl/1000).toFixed(1)}k` : '…'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    };

    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <button onClick={() => { if (view === 'results') setView('config'); else if (onBack) onBack(); }}
              className="app-card p-1.5 rounded-md flex-shrink-0" style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)` }}>
              <ArrowLeft className="w-4 h-4" style={{ color: colors.primaryText }} />
            </button>
            <div className="min-w-0">
              <h1 className="text-[17px] font-bold" style={{ color: colors.primaryText }}>Backtest Lab</h1>
              <p className="text-[13px]" style={{ color: colors.secondaryText }}>Production-grade strategy backtesting</p>
            </div>
          </div>
          <div className="flex overflow-x-auto space-x-1 rounded-lg p-0.5 self-start sm:self-auto" style={{ backgroundColor: colors.inputBg }}>
            {[
              { id: 'config', label: 'New Run' },
              { id: 'from-strategy', label: 'From Algo Flow' },
              { id: 'history', label: 'History' },
            ].map(t => (
              <button key={t.id} onClick={() => setView(t.id)}
                className="px-2 py-1 rounded-md text-[13px] font-medium transition-colors whitespace-nowrap"
                style={{
                  backgroundColor: view === t.id ? colors.accent : 'transparent',
                  color: view === t.id ? '#fff' : colors.secondaryText,
                }}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* -- CONFIG VIEW -- */}
        {view === 'config' && (
          <div className="space-y-3">
            {/* Strategy Mode Selector */}
            <div data-tour="backtest-strategy-form" className="rounded-xl p-3" style={cs}>
              <p className="text-[11px] font-semibold uppercase tracking-widest mb-3" style={{ color: colors.secondaryText }}>Strategy Mode</p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'visual', Icon: SlidersHorizontal, label: 'Visual Builder', sub: 'Select conditions', color: '#6366F1' },
                  { id: 'code',   Icon: Code2,             label: 'Code Editor',    sub: 'Write Python',       color: '#f59e0b' },
                  { id: 'ai',     Icon: Bot,               label: 'AI Generate',    sub: 'Describe in English', color: '#8b5cf6' },
                ].map(({ id, Icon, label, sub, color }) => {
                  const active = strategyMode === id;
                  return (
                    <button key={id} onClick={() => setStrategyMode(id)}
                      className="flex flex-col items-center gap-2 py-4 px-2 rounded-xl transition-all"
                      style={{
                        background: `linear-gradient(135deg, ${color}${active ? '28' : '15'} 0%, ${color}${active ? '10' : '05'} 100%)`,
                        border: `${active ? '2px' : '1px'} solid ${color}${active ? '70' : '28'}`,
                      }}>
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: `${color}${active ? '30' : '18'}` }}>
                        <Icon className="w-5 h-5" style={{ color }} />
                      </div>
                      <div className="text-center">
                        <p className="text-[13px] font-semibold leading-tight" style={{ color: active ? color : colors.primaryText }}>{label}</p>
                        <p className="text-[11px] mt-0.5" style={{ color: colors.secondaryText }}>{sub}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Mode toggle: Single / Portfolio */}
            <div className="rounded-xl p-1.5 flex gap-1" style={{ backgroundColor: colors.inputBg }}>
              {[{ id: 'single', label: 'Single Stock', sub: 'one symbol' }, { id: 'portfolio', label: 'Portfolio Mode', sub: 'shared pool' }].map(m => (
                <button key={m.id} onClick={() => setBacktestMode(m.id)}
                  className="flex-1 py-2 rounded-lg text-center transition-all"
                  style={{ backgroundColor: backtestMode === m.id ? colors.accent : 'transparent', color: backtestMode === m.id ? '#fff' : colors.secondaryText }}>
                  <p className="text-[13px] font-bold">{m.label}</p>
                  <p className="text-[11px] opacity-70">{m.sub}</p>
                </button>
              ))}
            </div>

            {/* Symbol — Single mode */}
            {backtestMode === 'single' && (
              <div className="rounded-xl p-3 space-y-2" style={cs}>
                <p className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: colors.secondaryText }}>Stock Symbol</p>
                <select className="w-full px-1.5 py-1 rounded text-[14px]" style={inputStyle}
                  value={symbol} onChange={e => setSymbol(e.target.value)}>
                  <option value="">— Select stock —</option>
                  {stocks.map(s => (
                    <option key={s.symbol} value={s.symbol}>{s.symbol.replace('.NS','')} — {s.company_name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Symbol — Portfolio mode */}
            {backtestMode === 'portfolio' && (
              <div className="rounded-xl p-3 space-y-2" style={cs}>
                <div className="flex items-center justify-between">
                  <p className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: colors.secondaryText }}>
                    Stocks ({portfolioSymbols.length}/20)
                  </p>
                  {watchlists.length > 0 && (
                    <select className="text-[11px] px-2 py-0.5 rounded" style={{ ...inputStyle, fontSize: 11 }}
                      defaultValue=""
                      onChange={e => {
                        const wl = watchlists.find(w => w.id === e.target.value);
                        if (wl) {
                          const toAdd = (wl.stocks || []).filter(s => !portfolioSymbols.includes(s)).slice(0, 20 - portfolioSymbols.length);
                          setPortfolioSymbols(prev => [...prev, ...toAdd]);
                        }
                        e.target.value = '';
                      }}>
                      <option value="">+ Load watchlist</option>
                      {watchlists.map(w => <option key={w.id} value={w.id}>{w.name} ({(w.stocks||[]).length})</option>)}
                    </select>
                  )}
                </div>

                {/* Selected chips */}
                {portfolioSymbols.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {portfolioSymbols.map(sym => (
                      <div key={sym} className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[12px] font-semibold"
                        style={{ backgroundColor: `${colors.accent}20`, border: `1px solid ${colors.accent}40`, color: colors.accent }}>
                        <span>{sym.replace('.NS','')}</span>
                        <button onClick={() => setPortfolioSymbols(prev => prev.filter(s => s !== sym))}
                          className="w-3.5 h-3.5 rounded-full flex items-center justify-center font-bold"
                          style={{ backgroundColor: `${colors.accent}30`, fontSize: 10 }}>—</button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add stock picker */}
                {portfolioSymbols.length < 20 && (
                  <div className="flex gap-2">
                    <select className="flex-1 px-1.5 py-1 rounded text-[13px]" style={inputStyle}
                      value={portfolioSymbolInput} onChange={e => setPortfolioSymbolInput(e.target.value)}>
                      <option value=""> Add stock …</option>
                      {stocks.filter(s => !portfolioSymbols.includes(s.symbol)).map(s => (
                        <option key={s.symbol} value={s.symbol}>{s.symbol.replace('.NS','')} — {s.company_name}</option>
                      ))}
                    </select>
                    <button onClick={() => { if (portfolioSymbolInput) { setPortfolioSymbols(prev => [...prev, portfolioSymbolInput]); setPortfolioSymbolInput(''); } }}
                      className="px-3 py-1 rounded text-[13px] font-bold"
                      style={{ backgroundColor: colors.accent, color: '#fff' }}>+</button>
                  </div>
                )}

                {/* Allocation info */}
                {portfolioSymbols.length > 0 && (
                  <div className="rounded-lg px-2.5 py-1.5 text-[12px]" style={{ backgroundColor: `${colors.accent}12` }}>
                    <span style={{ color: colors.secondaryText }}>?{Number(capital).toLocaleString('en-IN')} total — {maxPositions} slots = </span>
                    <span className="font-bold" style={{ color: colors.accent }}>?{Math.round(capital / maxPositions).toLocaleString('en-IN')} per position</span>
                    <span style={{ color: colors.secondaryText }}> — skips new entries when all slots full</span>
                  </div>
                )}

                {/* Max positions */}
                <div className="flex items-center gap-2">
                  <label className="text-[12px] flex-shrink-0" style={{ color: colors.secondaryText }}>
                    Max concurrent positions:
                  </label>
                  <div className="flex gap-1">
                    {[3,4,5,7,10].map(n => (
                      <button key={n} onClick={() => setMaxPositions(n)}
                        className="w-8 h-7 rounded text-[12px] font-bold transition-all"
                        style={{ backgroundColor: maxPositions === n ? colors.accent : colors.inputBg, color: maxPositions === n ? '#fff' : colors.secondaryText }}>
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Date range + Capital + Qty */}
            <div className="rounded-xl p-3 space-y-2" style={cs}>
              <p className="text-[11px] font-semibold uppercase tracking-widest mb-1" style={{ color: colors.secondaryText }}>Date Range & Capital</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="text-[13px]" style={{ color: colors.secondaryText }}>Start Date</label>
                  <input type="date" className="w-full px-1.5 py-1 rounded text-[13px] mt-0.5" style={inputStyle}
                    value={startDate} onChange={e => setStartDate(e.target.value)} />
                </div>
                <div>
                  <label className="text-[13px]" style={{ color: colors.secondaryText }}>End Date</label>
                  <input type="date" className="w-full px-1.5 py-1 rounded text-[13px] mt-0.5" style={inputStyle}
                    value={endDate} onChange={e => setEndDate(e.target.value)} />
                </div>
                <div>
                  <label className="text-[13px]" style={{ color: colors.secondaryText }}>Capital (?)</label>
                  <input type="number" className="w-full px-1.5 py-1 rounded text-[13px] mt-0.5" style={inputStyle}
                    value={capital} onChange={e => setCapital(e.target.value)} min={10000} step={10000} />
                </div>
                <div>
                  <label className="text-[13px]" style={{ color: colors.secondaryText }}>
                    {sizingMode === 'fixed' ? 'Qty per trade' : 'Auto-sized (risk %)'}
                  </label>
                  {sizingMode === 'fixed' ? (
                    <input type="number" className="w-full px-1.5 py-1 rounded text-[13px] mt-0.5" style={inputStyle}
                      value={quantity} onChange={e => setQuantity(e.target.value)} min={1} step={1} />
                  ) : (
                    <div className="px-1.5 py-1 rounded text-[13px] mt-0.5 font-medium" style={{ ...inputStyle, cursor: 'default' }}>
                      {riskPerTrade}% risk/trade
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* -- VISUAL MODE: Drag-Drop Builder -- */}
            {strategyMode === 'visual' && (() => {
              const cats = ['all', ...Array.from(new Set(availableConditions.map(c => c.category).filter(Boolean)))];
              const palette = activeCategory === 'all' ? availableConditions : availableConditions.filter(c => c.category === activeCategory);
              return (
                <div className="rounded-xl p-3 space-y-3" style={cs}>

                  {/* Templates row */}
                  <div>
                    <button onClick={() => setShowTemplates(v => !v)}
                      className="flex items-center gap-1.5 text-[12px] font-semibold mb-2"
                      style={{ color: colors.accent }}>
                      ? Strategy Templates {showTemplates ? '?' : '—'}
                    </button>
                    {showTemplates && (
                      <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
                        {availableTemplates.map(tpl => (
                          <div key={tpl.id}
                            className="flex-shrink-0 w-44 rounded-xl p-2.5 cursor-pointer transition-all"
                            style={{ backgroundColor: colors.inputBg, border: `1px solid ${colors.border}40` }}
                            onClick={() => {
                              setSelectedConditions(tpl.conditions || []);
                              setEntryLogic(tpl.entry_logic || 'ALL');
                              setExitConditions(tpl.exit_conditions || []);
                              setExitLogic(tpl.exit_logic || 'ANY');
                              setSlPct(tpl.stop_loss_pct || 5);
                              setTpPct(tpl.target_pct || 10);
                              setSignalExit(tpl.signal_exit || false);
                              setShowTemplates(false);
                            }}>
                            <p className="text-[12px] font-bold leading-tight" style={{ color: colors.primaryText }}>{tpl.name}</p>
                            <p className="text-[11px] mt-0.5 leading-snug" style={{ color: colors.secondaryText }}>{tpl.description}</p>
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {(tpl.tags || []).map(tag => (
                                <span key={tag} className="px-1.5 py-0.5 rounded text-[10px] font-semibold"
                                  style={{ backgroundColor: `${colors.accent}20`, color: colors.accent }}>
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Entry Header */}
                  <div className="flex items-center justify-between">
                    <p className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: colors.secondaryText }}>Entry Conditions</p>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px]" style={{ color: colors.secondaryText }}>Logic:</span>
                      <div className="flex rounded-lg overflow-hidden" style={{ border: `1px solid ${colors.accent}40` }}>
                        {['ALL', 'ANY'].map(l => (
                          <button key={l} onClick={() => setEntryLogic(l)}
                            className="px-3 py-1 text-[12px] font-bold transition-colors"
                            style={{ backgroundColor: entryLogic === l ? colors.accent : 'transparent', color: entryLogic === l ? '#fff' : colors.secondaryText }}>
                            {l}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Build zone — drop target + selected chips */}
                  <div
                    className="min-h-[60px] rounded-xl p-2 transition-all"
                    style={{
                      border: `2px dashed ${dragOverBuilder ? colors.accent : `${colors.accent}35`}`,
                      backgroundColor: dragOverBuilder ? `${colors.accent}08` : 'transparent',
                    }}
                    onDragOver={e => { e.preventDefault(); setDragOverBuilder(true); }}
                    onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverBuilder(false); }}
                    onDrop={e => {
                      e.preventDefault();
                      const id = draggedCondId.current;
                      if (id && !selectedConditions.includes(id)) setSelectedConditions(prev => [...prev, id]);
                      setDragOverBuilder(false);
                    }}>
                    {selectedConditions.length === 0 ? (
                      <p className="text-center text-[12px] py-3 select-none" style={{ color: `${colors.secondaryText}70` }}>
                        Drag conditions here — or tap + on any card below
                      </p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {selectedConditions.map(id => {
                          const cond = availableConditions.find(c => c.id === id);
                          if (!cond) return null;
                          const col = dirColor(cond);
                          return (
                            <div key={id} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-semibold select-none"
                              style={{ backgroundColor: `${col}18`, border: `1px solid ${col}45`, color: col }}>
                              <span>{cond.label}</span>
                              <button
                                onClick={() => setSelectedConditions(prev => prev.filter(c => c !== id))}
                                className="w-4 h-4 rounded-full flex items-center justify-center leading-none font-bold"
                                style={{ backgroundColor: `${col}30`, color: col, fontSize: 11 }}>
                                ×
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Logic hint */}
                  {selectedConditions.length > 1 && (
                    <p className="text-[11px] px-1" style={{ color: colors.secondaryText }}>
                      {entryLogic === 'ALL'
                        ? 'All conditions must be true on the same day to enter'
                        : 'Any one condition being true on the entry day is enough'}
                    </p>
                  )}

                  {/* Category filter pills */}
                  <div className="flex gap-1.5 overflow-x-auto pb-0.5" style={{ scrollbarWidth: 'none' }}>
                    {cats.map(cat => (
                      <button key={cat} onClick={() => setActiveCategory(cat)}
                        className="flex-shrink-0 px-2.5 py-1 rounded-full text-[11px] font-semibold capitalize transition-all"
                        style={{
                          backgroundColor: activeCategory === cat ? colors.accent : colors.inputBg,
                          color: activeCategory === cat ? '#fff' : colors.secondaryText,
                        }}>
                        {cat === 'all' ? 'All' : cat}
                      </button>
                    ))}
                  </div>

                  {/* -- Exit Conditions -- */}
                  <div className="pt-1 border-t" style={{ borderColor: colors.border + '40' }}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: colors.secondaryText }}>
                        Exit Conditions <span className="normal-case font-normal">(optional)</span>
                      </p>
                      <div className="flex items-center gap-2">
                        <span className="text-[11px]" style={{ color: colors.secondaryText }}>Logic:</span>
                        <div className="flex rounded-lg overflow-hidden" style={{ border: `1px solid ${colors.accent}40` }}>
                          {['ANY', 'ALL'].map(l => (
                            <button key={l} onClick={() => setExitLogic(l)}
                              className="px-2.5 py-0.5 text-[11px] font-bold transition-colors"
                              style={{ backgroundColor: exitLogic === l ? '#ef4444' : 'transparent', color: exitLogic === l ? '#fff' : colors.secondaryText }}>
                              {l}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div
                      className="min-h-[48px] rounded-xl p-2 transition-all"
                      style={{
                        border: `2px dashed ${dragOverExitBuilder ? '#ef4444' : '#ef444435'}`,
                        backgroundColor: dragOverExitBuilder ? '#ef444408' : 'transparent',
                      }}
                      onDragOver={e => { e.preventDefault(); setDragOverExitBuilder(true); }}
                      onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverExitBuilder(false); }}
                      onDrop={e => {
                        e.preventDefault();
                        const id = draggedExitCondId.current || draggedCondId.current;
                        if (id && !exitConditions.includes(id)) setExitConditions(prev => [...prev, id]);
                        setDragOverExitBuilder(false);
                      }}>
                      {exitConditions.length === 0 ? (
                        <p className="text-center text-[11px] py-2 select-none" style={{ color: '#ef444470' }}>
                          Drag conditions here to exit when they fire — leave empty for SL/TP only
                        </p>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {exitConditions.map(id => {
                            const cond = availableConditions.find(c => c.id === id);
                            if (!cond) return null;
                            return (
                              <div key={id} className="flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold select-none"
                                style={{ backgroundColor: '#ef444418', border: '1px solid #ef444445', color: '#ef4444' }}>
                                <span>{cond.label}</span>
                                <button onClick={() => setExitConditions(prev => prev.filter(c => c !== id))}
                                  className="w-3.5 h-3.5 rounded-full flex items-center justify-center leading-none font-bold"
                                  style={{ backgroundColor: '#ef444430', fontSize: 10 }}>—</button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    {exitConditions.length > 0 && (
                      <p className="text-[11px] px-1 mt-1" style={{ color: colors.secondaryText }}>
                        Exit fires when {exitLogic === 'ANY' ? 'any one of these conditions' : 'all of these conditions'} trigger — checked daily against prior-day features
                      </p>
                    )}
                  </div>

                  {/* Condition card palette — max 7 visible, internally scrollable */}
                  <div className="space-y-1.5 overflow-y-auto pr-0.5" style={{ maxHeight: '392px', scrollbarWidth: 'thin', scrollbarColor: `${colors.accent}40 transparent` }}>
                    {palette.map(c => {
                      const selected = selectedConditions.includes(c.id);
                      const col = dirColor(c);
                      return (
                        <div key={c.id}
                          draggable
                          onDragStart={() => { draggedCondId.current = c.id; }}
                          onDragEnd={() => { draggedCondId.current = null; }}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all select-none"
                          style={{
                            backgroundColor: selected ? `${col}10` : colors.inputBg,
                            border: `1px solid ${selected ? col : colors.border}${selected ? '50' : '40'}`,
                            cursor: 'grab',
                          }}>
                          {/* Direction indicator */}
                          <div className="flex-shrink-0 w-2 h-2 rounded-full mt-0.5" style={{ backgroundColor: col }} />

                          {/* Text */}
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] font-semibold leading-tight" style={{ color: selected ? col : colors.primaryText }}>
                              {c.label}
                            </p>
                            <p className="text-[12px] mt-0.5 leading-snug" style={{ color: colors.secondaryText }}>
                              {c.description}
                            </p>
                          </div>

                          {/* Add / Remove button */}
                          <button
                            onClick={() => setSelectedConditions(prev =>
                              selected ? prev.filter(id => id !== c.id) : [...prev, c.id]
                            )}
                            className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center font-bold transition-all"
                            style={{
                              backgroundColor: selected ? `${col}20` : `${colors.accent}20`,
                              color: selected ? col : colors.accent,
                              fontSize: selected ? 16 : 20,
                            }}>
                            {selected ? '?' : '+'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* -- CODE MODE: Code Editor -- */}
            {strategyMode === 'code' && (
              <div className="rounded-xl p-3 space-y-2" style={cs}>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: colors.secondaryText }}>Strategy Code (Python)</p>
                  <button onClick={handleValidateCode}
                    className="px-1.5 py-1 rounded text-[13px] font-semibold"
                    style={{ backgroundColor: colors.accent, color: '#fff' }}>
                    Validate
                  </button>
                </div>
                <textarea
                  className="w-full px-3 py-1 rounded-lg text-[13px] leading-relaxed"
                  style={{
                    ...inputStyle,
                    fontFamily: 'ui-monospace, "Cascadia Code", "Fira Code", Menlo, monospace',
                    minHeight: '280px',
                    resize: 'vertical',
                    tabSize: 4,
                    whiteSpace: 'pre',
                    overflowWrap: 'normal',
                    overflowX: 'auto',
                  }}
                  value={strategyCode}
                  onChange={e => { setStrategyCode(e.target.value); setCodeValid(null); }}
                  onKeyDown={e => {
                    if (e.key === 'Tab') {
                      e.preventDefault();
                      const start = e.target.selectionStart;
                      const end = e.target.selectionEnd;
                      const val = e.target.value;
                      setStrategyCode(val.substring(0, start) + '    ' + val.substring(end));
                      setTimeout(() => { e.target.selectionStart = e.target.selectionEnd = start + 4; }, 0);
                    }
                  }}
                  spellCheck="false"
                />
                {codeValid && (
                  <div className="flex items-center gap-2 py-1">
                    {codeValid.valid ? (
                      <>
                        <CheckCircle className="w-3.5 h-3.5" style={{ color: '#16C784' }} />
                        <span className="text-[13px] font-medium" style={{ color: '#16C784' }}>
                          Code valid — entry: {codeValid.has_entry ? '?' : '—'}, exit: {codeValid.has_exit ? '?' : '—'}
                        </span>
                      </>
                    ) : (
                      <>
                        <XCircle className="w-3.5 h-3.5" style={{ color: '#ef4444' }} />
                        <span className="text-[13px] font-medium" style={{ color: '#ef4444' }}>{codeValid.error}</span>
                      </>
                    )}
                  </div>
                )}
                {/* Code Guide */}
                <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${colors.accent}25` }}>
                  <button
                    onClick={() => setShowCodeGuide(g => !g)}
                    className="w-full flex items-center justify-between px-3 py-2"
                    style={{ backgroundColor: `${colors.accent}10` }}>
                    <span className="text-[12px] font-semibold" style={{ color: colors.accent }}>
                      ?? Code Guide — how to write valid strategy code
                    </span>
                    <span className="text-[11px]" style={{ color: colors.secondaryText }}>{showCodeGuide ? '? hide' : '? show'}</span>
                  </button>

                  {showCodeGuide && (
                    <div className="px-3 py-3 space-y-4 text-[12px]" style={{ color: colors.secondaryText }}>

                      {/* Required structure */}
                      <div>
                        <p className="font-bold mb-1.5" style={{ color: colors.primaryText }}>Required structure</p>
                        <pre className="rounded-lg px-3 py-2.5 leading-relaxed overflow-x-auto"
                          style={{ backgroundColor: colors.inputBg, color: colors.primaryText, fontFamily: 'monospace', fontSize: 12 }}>
{`def should_enter(candle, features, portfolio):
    # return True to open a BUY position
    return False

def should_exit(candle, features, position):
    # return True to close the open position
    # (optional — SL/TP will still apply)
    return False`}
                        </pre>
                        <p className="mt-1.5">
                          <span style={{ color: colors.accent }}>should_enter</span> is required.{' '}
                          <span style={{ color: colors.accent }}>should_exit</span> is optional — if you skip it, the engine exits on SL or TP only.
                        </p>
                        <p className="mt-1.5">
                          You can also define <span style={{ color: colors.accent }}>helper functions</span> above the main ones — they are fully accessible:
                        </p>
                        <pre className="rounded-lg px-3 py-2 mt-1.5 overflow-x-auto"
                          style={{ backgroundColor: colors.inputBg, color: colors.primaryText, fontFamily: 'monospace', fontSize: 12 }}>
{`def is_trending(features):
    return features.get("adx", 0) > 25

def should_enter(candle, features, portfolio):
    return is_trending(features) and features.get("rsi_14", 50) < 35`}
                        </pre>
                        <div className="mt-2 rounded-lg px-3 py-2" style={{ backgroundColor: '#f59e0b12', border: '1px solid #f59e0b30' }}>
                          <p style={{ color: '#f59e0b' }}>
                            ? Scanner signals (double_bottom, bull_flag, ascending_triangle, etc.) are <strong>not available</strong> in the code editor yet.
                            Only the indicator features listed below are passed to your functions.
                          </p>
                        </div>
                      </div>

                      {/* Available data */}
                      <div>
                        <p className="font-bold mb-1.5" style={{ color: colors.primaryText }}>Available data</p>
                        <div className="space-y-2">
                          {[
                            {
                              arg: 'candle',
                              keys: [
                                ['open', 'open price of current day'],
                                ['high', 'day high'],
                                ['low', 'day low'],
                                ['close', 'close price of current day'],
                                ['volume', 'today\'s traded volume'],
                                ['date', 'date string YYYY-MM-DD'],
                              ],
                            },
                            {
                              arg: 'features',
                              note: 'Prior-day computed indicators (no look-ahead)',
                              keys: [
                                ['rsi_14', 'RSI(14) — 0 to 100'],
                                ['macd_line', 'MACD line value'],
                                ['macd_signal', 'MACD signal line value'],
                                ['sma_50', '50-day simple moving average'],
                                ['volume_ratio', 'today vol — 20-day avg vol'],
                                ['supertrend_signal', '"BUY" or "SELL"'],
                                ['adx', 'ADX trend strength — 0 to 100'],
                              ],
                            },
                            {
                              arg: 'portfolio',
                              note: 'Only in should_enter',
                              keys: [
                                ['equity', 'current portfolio value (?)'],
                                ['position_open', 'True if already holding a trade'],
                                ['capital_initial', 'starting capital (?)'],
                              ],
                            },
                            {
                              arg: 'position',
                              note: 'Only in should_exit',
                              keys: [
                                ['entry_price', 'price at which trade was opened'],
                                ['entry_date', 'date string YYYY-MM-DD'],
                                ['unrealized_pnl_pct', 'current open P&L in %'],
                                ['holding_days', 'number of days trade is open'],
                              ],
                            },
                          ].map(({ arg, note, keys }) => (
                            <div key={arg}>
                              <p className="font-semibold mb-1" style={{ color: colors.accent }}>
                                {arg}{note && <span className="font-normal ml-1" style={{ color: colors.secondaryText }}> {note}</span>}
                              </p>
                              <div className="rounded-lg overflow-hidden" style={{ border: `1px solid ${colors.border}40` }}>
                                {keys.map(([k, desc], i) => (
                                  <div key={k} className="flex items-start gap-2 px-2.5 py-1.5"
                                    style={{ backgroundColor: i % 2 === 0 ? `${colors.inputBg}` : 'transparent' }}>
                                    <code className="flex-shrink-0 font-bold" style={{ color: colors.primaryText, fontFamily: 'monospace' }}>
                                      .get("{k}")
                                    </code>
                                    <span style={{ color: colors.secondaryText }}>{desc}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* What's NOT allowed */}
                      <div>
                        <p className="font-bold mb-1.5" style={{ color: '#ef4444' }}>What will fail validation</p>
                        <div className="rounded-lg px-3 py-2 space-y-1" style={{ backgroundColor: '#ef444410', border: '1px solid #ef444430' }}>
                          {[
                            'import os  /  import requests  /  any import',
                            'class definitions',
                            'eval, exec, open, os, sys, getattr',
                            'async / await',
                            'global / nonlocal',
                            'Code longer than 10,000 characters',
                          ].map(item => (
                            <p key={item} style={{ color: '#ef4444' }}>? {item}</p>
                          ))}
                        </div>
                        <p className="mt-1.5">
                          Math functions available without importing:{' '}
                          <code style={{ color: colors.accent }}>sqrt, log, exp, ceil, floor, abs, min, max, round</code>
                        </p>
                      </div>

                      {/* Examples */}
                      <div>
                        <p className="font-bold mb-1.5" style={{ color: colors.primaryText }}>Examples</p>
                        <div className="space-y-2">
                          {[
                            {
                              title: 'RSI reversal + MACD confirmation',
                              code:
`def should_enter(candle, features, portfolio):
    rsi = features.get("rsi_14", 50)
    macd = features.get("macd_line", 0)
    sig  = features.get("macd_signal", 0)
    return rsi < 30 and macd > sig

def should_exit(candle, features, position):
    rsi  = features.get("rsi_14", 50)
    days = position.get("holding_days", 0)
    return rsi > 65 or days >= 10`,
                            },
                            {
                              title: 'SuperTrend trend-follow',
                              code:
`def should_enter(candle, features, portfolio):
    trend = features.get("supertrend_signal", "")
    close = candle.get("close", 0)
    sma   = features.get("sma_50", 0)
    return trend == "BUY" and close > sma

def should_exit(candle, features, position):
    return features.get("supertrend_signal") == "SELL"`,
                            },
                            {
                              title: 'Volume breakout (no exit signal needed)',
                              code:
`def should_enter(candle, features, portfolio):
    vol_ratio = features.get("volume_ratio", 1)
    adx       = features.get("adx", 0)
    return vol_ratio > 2.5 and adx > 25`,
                            },
                          ].map(ex => (
                            <div key={ex.title}>
                              <p className="font-semibold mb-1" style={{ color: colors.secondaryText }}>{ex.title}</p>
                              <pre className="rounded-lg px-3 py-2 overflow-x-auto leading-relaxed"
                                style={{ backgroundColor: colors.inputBg, color: colors.primaryText, fontFamily: 'monospace', fontSize: 11 }}>
                                {ex.code}
                              </pre>
                              <button
                                onClick={() => { setStrategyCode(ex.code); setCodeValid(null); }}
                                className="mt-1 text-[11px] font-semibold"
                                style={{ color: colors.accent }}>
                                ? Use this template
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>

                    </div>
                  )}
                </div>
              </div>
            )}

            {/* -- AI MODE: Prompt Input -- */}
            {strategyMode === 'ai' && (
              <div className="rounded-xl p-3 space-y-2" style={cs}>
                <p className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: colors.secondaryText }}>Describe Your Strategy</p>
                <textarea
                  className="w-full px-3 py-1 rounded-lg text-[13px] leading-relaxed"
                  style={{ ...inputStyle, minHeight: '100px', resize: 'vertical' }}
                  placeholder="e.g., Buy when RSI drops below 30 and MACD crosses above signal line. Exit when RSI goes above 70 or after holding for 15 days. Focus on momentum reversals with volume confirmation."
                  value={aiPrompt}
                  onChange={e => setAiPrompt(e.target.value)}
                />
                <button onClick={handleAiGenerate} disabled={aiGenerating || !aiPrompt.trim()}
                  className="w-full py-1 rounded-lg text-[13px] font-semibold transition-opacity flex items-center justify-center gap-2"
                  style={{
                    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                    color: '#fff',
                    opacity: (aiGenerating || !aiPrompt.trim()) ? 0.5 : 1,
                  }}>
                  <Sparkles className="w-3.5 h-3.5" />
                  {aiGenerating ? 'Generating strategy…' : 'Generate with AI'}
                </button>
                {strategyCode && strategyMode === 'ai' && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-[13px] font-semibold" style={{ color: '#16C784' }}>Generated code (review & edit before running):</p>
                      <button onClick={() => setStrategyMode('code')} className="text-[13px] font-medium" style={{ color: colors.accent }}>
                        Open in Editor ?
                      </button>
                    </div>
                    <pre className="px-3 py-1 rounded-lg text-[13px] leading-relaxed overflow-x-auto max-h-48 overflow-y-auto"
                      style={{ backgroundColor: colors.inputBg, color: colors.primaryText, fontFamily: 'monospace' }}>
                      {strategyCode}
                    </pre>
                    {codeValid && !codeValid.valid && (
                      <p className="text-[13px] mt-1" style={{ color: '#ef4444' }}>? {codeValid.error}</p>
                    )}
                  </div>
                )}
                <div className="rounded-md p-1.5" style={{ backgroundColor: colors.inputBg }}>
                  <p className="text-[12px]" style={{ color: colors.secondaryText }}>
                    Describe any entry/exit logic in plain English. The AI generates Python code you can review and edit before running. No coding skills needed.
                  </p>
                </div>
              </div>
            )}

            {/* Exit Parameters */}
            <div className="rounded-xl p-3 space-y-3" style={cs}>
              <p className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: colors.secondaryText }}>Exit Parameters</p>
              <div>
                <label className="text-[13px] mb-1 block" style={{ color: colors.secondaryText }}>
                  Stop-Loss: <span style={{ color: colors.loss }}>{slPct}%</span>
                </label>
                <input type="range" min="1" max="20" step="0.5" value={slPct}
                  onChange={e => setSlPct(parseFloat(e.target.value))}
                  className="w-full" style={{ accentColor: colors.loss }} />
              </div>
              <div>
                <label className="text-[13px] mb-1 block" style={{ color: colors.secondaryText }}>
                  Target: <span style={{ color: colors.profit }}>{tpPct}%</span>
                </label>
                <input type="range" min="2" max="50" step="0.5" value={tpPct}
                  onChange={e => setTpPct(parseFloat(e.target.value))}
                  className="w-full" style={{ accentColor: colors.profit }} />
              </div>
              {trailingSl > 0 && (
                <div>
                  <label className="text-[13px] mb-1 block" style={{ color: colors.secondaryText }}>
                    Trailing SL: <span style={{ color: '#f59e0b' }}>{trailingSl}%</span>
                  </label>
                  <input type="range" min="0.5" max="15" step="0.5" value={trailingSl}
                    onChange={e => setTrailingSl(parseFloat(e.target.value))}
                    className="w-full" style={{ accentColor: '#f59e0b' }} />
                </div>
              )}
              {strategyMode === 'visual' && (
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input type="checkbox" checked={signalExit} onChange={e => setSignalExit(e.target.checked)}
                    style={{ accentColor: colors.accent }} />
                  <span className="text-[13px]" style={{ color: colors.secondaryText }}>Exit on SuperTrend SELL signal</span>
                </label>
              )}
            </div>

            {/* Advanced Settings (Collapsible) */}
            <div className="rounded-xl overflow-hidden" style={cs}>
              <button onClick={() => setShowAdvanced(!showAdvanced)}
                className="w-full px-3 py-1 flex items-center justify-between"
                style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)` }}>
                <span className="text-[13px] font-semibold" style={{ color: colors.secondaryText }}>
                  Advanced Settings {showAdvanced ? '?' : '—'}
                </span>
                <span className="hidden sm:inline text-[12px]" style={{ color: colors.accent }}>Slippage — Commission — Position Sizing</span>
              </button>
              {showAdvanced && (
                <div className="px-3 pb-1.5 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <label className="text-[13px]" style={{ color: colors.secondaryText }}>Slippage (%)</label>
                      <input type="number" className="w-full px-1.5 py-1 rounded text-[13px] mt-0.5" style={inputStyle}
                        value={slippagePct} onChange={e => setSlippagePct(e.target.value)} min={0} max={1} step={0.01} />
                    </div>
                    <div>
                      <label className="text-[13px]" style={{ color: colors.secondaryText }}>Commission (?/order)</label>
                      <input type="number" className="w-full px-1.5 py-1 rounded text-[13px] mt-0.5" style={inputStyle}
                        value={commissionPerOrder} onChange={e => setCommissionPerOrder(e.target.value)} min={0} step={5} />
                    </div>
                  </div>
                  <div>
                    <label className="text-[13px] mb-1 block" style={{ color: colors.secondaryText }}>
                      Trailing Stop Loss: <span style={{ color: trailingSl > 0 ? '#f59e0b' : colors.secondaryText }}>{trailingSl > 0 ? `${trailingSl}%` : 'Off'}</span>
                    </label>
                    <input type="range" min="0" max="15" step="0.5" value={trailingSl}
                      onChange={e => setTrailingSl(parseFloat(e.target.value))}
                      className="w-full" style={{ accentColor: '#f59e0b' }} />
                  </div>
                  <div>
                    <p className="text-[13px] font-semibold mb-1" style={{ color: colors.secondaryText }}>Position Sizing</p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      {[
                        { id: 'fixed', label: 'Fixed Quantity' },
                        { id: 'risk_pct', label: 'Risk % per Trade' },
                      ].map(s => (
                        <button key={s.id} onClick={() => setSizingMode(s.id)}
                          className="flex-1 py-1 rounded text-[13px] font-semibold transition-colors"
                          style={{ backgroundColor: sizingMode === s.id ? colors.accent : colors.inputBg, color: sizingMode === s.id ? '#fff' : colors.secondaryText }}>
                          {s.label}
                        </button>
                      ))}
                    </div>
                    {sizingMode === 'risk_pct' && (
                      <div className="mt-2">
                        <label className="text-[13px]" style={{ color: colors.secondaryText }}>
                          Risk per trade: <span style={{ color: colors.accent }}>{riskPerTrade}%</span> of equity
                        </label>
                        <input type="range" min="0.5" max="5" step="0.5" value={riskPerTrade}
                          onChange={e => setRiskPerTrade(parseFloat(e.target.value))}
                          className="w-full" style={{ accentColor: colors.accent }} />
                        <p className="text-[12px] mt-0.5" style={{ color: colors.secondaryText }}>
                          Qty auto-calculated: SL hit = {riskPerTrade}% of equity lost
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Run name */}
            <div className="rounded-xl p-3" style={cs}>
              <label className="text-[11px] font-semibold uppercase tracking-widest mb-2 block" style={{ color: colors.secondaryText }}>Run name (optional)</label>
              <input type="text" placeholder="e.g. MACD + RSI Oversold test"
                className="w-full px-1.5 py-1 rounded text-[13px]" style={inputStyle}
                value={runName} onChange={e => setRunName(e.target.value)} />
            </div>

            {error && <p className="text-[13px] text-center" style={{ color: colors.loss }}>{error}</p>}

            {backtestMode === 'single' ? (
              <button onClick={handleRun}
                data-tour="backtest-run-btn"
                disabled={loading || !symbol || (strategyMode === 'visual' && selectedConditions.length === 0) || ((strategyMode === 'code' || strategyMode === 'ai') && !strategyCode.trim())}
                className="w-full py-2 rounded-lg text-[14px] font-semibold transition-opacity"
                style={{ backgroundColor: colors.accent, color: '#fff', opacity: loading ? 0.5 : 1 }}>
                {loading ? 'Running backtest…' : '?  Run Backtest'}
              </button>
            ) : (
              <button onClick={handlePortfolioRun}
                data-tour="backtest-run-btn"
                disabled={portfolioLoading || portfolioSymbols.length === 0 || selectedConditions.length === 0}
                className="w-full py-2 rounded-lg text-[14px] font-semibold transition-opacity"
                style={{ backgroundColor: '#8b5cf6', color: '#fff', opacity: portfolioLoading ? 0.5 : 1 }}>
                {portfolioLoading ? `Running ${portfolioSymbols.length} stocks…` : `?  Run Portfolio Backtest (${portfolioSymbols.length} stocks)`}
              </button>
            )}

            <div className="rounded-lg p-1.5" style={{ backgroundColor: colors.inputBg }}>
              <p className="text-[13px]" style={{ color: colors.secondaryText }}>
                <span className="font-semibold" style={{ color: colors.accent }}>How it works: </span>
                {strategyMode === 'visual' ? 'Conditions evaluated on prior-day features to prevent look-ahead bias. Entry at next day open. SL/TP checked against day high/low.' :
                 strategyMode === 'code' ? 'Your should_enter() runs each day with prior-day features. should_exit() runs when a position is open. SL/TP/trailing enforced by engine.' :
                 'AI generates Python strategy code from your description. Review generated code, then run. Same execution path as Code mode.'}
                {' '}Slippage ({slippagePct}%) and commission (?{commissionPerOrder}/order) are applied.
              </p>
            </div>
          </div>
        )}

        {/* -- RESULTS VIEW -- */}
        {view === 'results' && results && (
          <div data-tour="backtest-results" className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-[14px] font-semibold" style={{ color: colors.primaryText }}>
                {results.name || results.symbol} &nbsp;
                <span className="text-[13px] font-normal" style={{ color: colors.secondaryText }}>
                  {results.start_date} → {results.end_date}
                </span>
              </p>
              <div className="flex items-center gap-2">
                {results.mode && (
                  <span className="text-[12px] font-bold px-3 py-1 rounded-full"
                    style={{
                      backgroundColor: results.mode === 'visual' ? '#6366F120' : results.mode === 'code' ? '#f59e0b20' : '#8b5cf620',
                      color: results.mode === 'visual' ? '#6366F1' : results.mode === 'code' ? '#f59e0b' : '#8b5cf6',
                    }}>
                    {results.mode === 'visual' ? 'VISUAL' : results.mode === 'code' ? 'CODE' : 'AI'}
                  </span>
                )}
                <button
                  onClick={() => setSocialOpen(true)}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold"
                  style={{ background: 'rgba(99,102,241,0.15)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.3)' }}>
                  <Share2 className="w-3 h-3" /> Post
                </button>
                <button
                  onClick={() => setShareOpen(true)}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold"
                  style={{ background: 'rgba(245,158,11,0.15)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.3)' }}>
                  <Share2 className="w-3 h-3" /> Forum
                </button>
              </div>
            </div>

            {/* Primary metrics — 3 top cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {[
                { label: 'Return', value: pct(results.total_return_pct), color: (results.total_return_pct || 0) >= 0 ? colors.profit : colors.loss },
                { label: 'Win Rate', value: pct(results.win_rate_pct), color: colors.accent },
                { label: 'Max DD', value: pct(results.max_drawdown_pct), color: colors.loss },
              ].map(m => (
                <div key={m.label} className="rounded-lg p-1.5 text-center" style={cs}>
                  <p className="text-[13px]" style={{ color: colors.secondaryText }}>{m.label}</p>
                  <p className="text-[14px] font-bold" style={{ color: m.color }}>{m.value}</p>
                </div>
              ))}
            </div>

            {/* Extended metrics grid */}
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: 'Trades', value: results.num_trades ?? '…' },
                { label: 'Wins / Losses', value: `${results.num_winning_trades ?? 0} / ${results.num_losing_trades ?? 0}` },
                { label: 'Final Capital', value: formatPrice(results.capital_final) },
                { label: 'Sharpe Ratio', value: results.sharpe_ratio != null ? parseFloat(results.sharpe_ratio).toFixed(2) : '…' },
                { label: 'Sortino Ratio', value: results.sortino_ratio != null ? parseFloat(results.sortino_ratio).toFixed(2) : '…' },
                { label: 'Profit Factor', value: results.profit_factor != null ? parseFloat(results.profit_factor).toFixed(2) : '…' },
                { label: 'Avg Win', value: results.avg_win_pct != null ? `+${parseFloat(results.avg_win_pct).toFixed(2)}%` : '…' },
                { label: 'Avg Loss', value: results.avg_loss_pct != null ? `${parseFloat(results.avg_loss_pct).toFixed(2)}%` : '…' },
                { label: 'Expectancy', value: results.expectancy != null ? `${formatPrice(results.expectancy, 0)}/trade` : '…' },
                { label: 'Avg Holding', value: results.avg_holding_days != null ? `${results.avg_holding_days} days` : '…' },
                { label: 'CAGR', value: results.cagr_pct != null ? `${parseFloat(results.cagr_pct).toFixed(2)}%` : '…' },
                { label: 'Commission', value: results.total_commission != null ? formatPrice(results.total_commission, 0) : '…' },
              ].map(m => (
                <div key={m.label} className="rounded-lg p-1.5 flex justify-between items-center" style={cs}>
                  <span className="text-[13px]" style={{ color: colors.secondaryText }}>{m.label}</span>
                  <span className="text-[14px] font-semibold" style={{ color: colors.primaryText }}>{m.value}</span>
                </div>
              ))}
            </div>

            {/* Benchmark comparison */}
            {results.benchmark_return_pct != null && (
              <div className="rounded-lg p-1.5 flex items-center justify-between" style={{ ...cs, borderColor: '#6366f140' }}>
                <div>
                  <p className="text-[13px]" style={{ color: colors.secondaryText }}>Buy & Hold Benchmark</p>
                  <p className="text-[13px] font-bold" style={{ color: (results.benchmark_return_pct || 0) >= 0 ? colors.profit : colors.loss }}>
                    {pct(results.benchmark_return_pct)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[13px]" style={{ color: colors.secondaryText }}>Strategy Alpha</p>
                  <p className="text-[13px] font-bold" style={{
                    color: ((results.total_return_pct || 0) - (results.benchmark_return_pct || 0)) >= 0 ? colors.profit : colors.loss
                  }}>
                    {pct((results.total_return_pct || 0) - (results.benchmark_return_pct || 0))}
                  </p>
                </div>
              </div>
            )}

            {/* Yearly P&L Breakdown */}
            {(results.trades || []).length >= 2 && (() => {
              const yearMap = {};
              for (const t of results.trades) {
                const yr = (t.entry_date || '').slice(0, 4) || 'Unknown';
                if (!yearMap[yr]) yearMap[yr] = { trades: 0, wins: 0, pnl: 0, best: null, worst: null };
                const m = yearMap[yr];
                m.trades++;
                if ((t.pnl || 0) > 0) m.wins++;
                m.pnl += t.pnl || 0;
                m.best  = m.best  == null ? (t.pnl || 0) : Math.max(m.best,  t.pnl || 0);
                m.worst = m.worst == null ? (t.pnl || 0) : Math.min(m.worst, t.pnl || 0);
              }
              const years = Object.keys(yearMap).sort();
              if (years.length < 2) return null;
              return (
                <div className="rounded-lg p-1.5" style={cs}>
                  <p className="text-[13px] font-semibold mb-2" style={{ color: colors.secondaryText }}>Year-by-Year P&amp;L</p>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${colors.border}60` }}>
                          {['Year', 'Trades', 'Wins', 'Win %', 'Net P&L', 'Best', 'Worst'].map(h => (
                            <th key={h} style={{ color: colors.secondaryText, fontWeight: 600, textAlign: h === 'Year' ? 'left' : 'right', padding: '4px 8px', whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {years.map(yr => {
                          const m = yearMap[yr];
                          const winPct = m.trades > 0 ? Math.round((m.wins / m.trades) * 100) : 0;
                          const pnlColor = m.pnl >= 0 ? colors.profit : colors.loss;
                          return (
                            <tr key={yr} style={{ borderBottom: `1px solid ${colors.border}30` }}>
                              <td style={{ color: colors.primaryText, fontWeight: 700, padding: '5px 8px' }}>{yr}</td>
                              <td style={{ color: colors.primaryText, textAlign: 'right', padding: '5px 8px' }}>{m.trades}</td>
                              <td style={{ color: colors.secondaryText, textAlign: 'right', padding: '5px 8px' }}>{m.wins}</td>
                              <td style={{ color: winPct >= 50 ? colors.profit : colors.loss, textAlign: 'right', padding: '5px 8px', fontWeight: 600 }}>{winPct}%</td>
                              <td style={{ color: pnlColor, textAlign: 'right', padding: '5px 8px', fontWeight: 700 }}>{m.pnl >= 0 ? '+' : ''}₹{Math.abs(Math.round(m.pnl)).toLocaleString('en-IN')}</td>
                              <td style={{ color: colors.profit, textAlign: 'right', padding: '5px 8px' }}>+₹{Math.abs(Math.round(m.best || 0)).toLocaleString('en-IN')}</td>
                              <td style={{ color: colors.loss, textAlign: 'right', padding: '5px 8px' }}>-₹{Math.abs(Math.round(m.worst || 0)).toLocaleString('en-IN')}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}

            {/* Exit Outcomes Breakdown */}
            {(results.trades || []).length >= 2 && (() => {
              const outcomeMap = {};
              for (const t of results.trades) {
                const reason = t.exit_reason || 'OTHER';
                if (!outcomeMap[reason]) outcomeMap[reason] = { count: 0, pnl: 0 };
                outcomeMap[reason].count++;
                outcomeMap[reason].pnl += t.pnl || 0;
              }
              const total = results.trades.length;
              const rows = Object.entries(outcomeMap).sort((a, b) => b[1].count - a[1].count);
              const labelMap = { TP: 'Target Hit', SL: 'Stop Loss', SIGNAL: 'Signal Exit', END: 'End of Period' };
              const colorMap = { TP: colors.profit, SL: colors.loss, SIGNAL: colors.accent, END: '#6b7280' };
              return (
                <div className="rounded-lg p-1.5" style={cs}>
                  <p className="text-[13px] font-semibold mb-2" style={{ color: colors.secondaryText }}>Exit Outcomes Breakdown</p>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${colors.border}60` }}>
                          {['Exit Type', 'Count', 'Share', 'Total P&L', 'Avg / Trade'].map(h => (
                            <th key={h} style={{ color: colors.secondaryText, fontWeight: 600, textAlign: h === 'Exit Type' ? 'left' : 'right', padding: '4px 8px', whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map(([reason, m]) => {
                          const col = colorMap[reason] || '#6b7280';
                          const share = Math.round((m.count / total) * 100);
                          const avg = m.count > 0 ? m.pnl / m.count : 0;
                          return (
                            <tr key={reason} style={{ borderBottom: `1px solid ${colors.border}30` }}>
                              <td style={{ padding: '5px 8px' }}>
                                <span style={{ background: `${col}18`, color: col, borderRadius: 6, padding: '2px 8px', fontWeight: 700, fontSize: 12 }}>{labelMap[reason] || reason}</span>
                              </td>
                              <td style={{ color: colors.primaryText, textAlign: 'right', padding: '5px 8px', fontWeight: 600 }}>{m.count}</td>
                              <td style={{ color: colors.secondaryText, textAlign: 'right', padding: '5px 8px' }}>{share}%</td>
                              <td style={{ color: m.pnl >= 0 ? colors.profit : colors.loss, textAlign: 'right', padding: '5px 8px', fontWeight: 700 }}>{m.pnl >= 0 ? '+' : ''}₹{Math.abs(Math.round(m.pnl)).toLocaleString('en-IN')}</td>
                              <td style={{ color: avg >= 0 ? colors.profit : colors.loss, textAlign: 'right', padding: '5px 8px' }}>{avg >= 0 ? '+' : ''}₹{Math.abs(Math.round(avg)).toLocaleString('en-IN')}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}

            {/* Monthly returns heatmap */}
            {renderMonthlyReturns(results.monthly_returns)}

            {/* Chart with Replay Controls */}
            {results.symbol && (
              <div className="space-y-2">
                {(results.trades || []).length > 0 && (
                  <BacktestReplayControls
                    trades={results.trades}
                    candleDates={candleDates}
                    onReplayDateChange={setReplayUpToDate}
                    onTradeSelect={setHighlightedTradeIndex}
                    colors={colors}
                  />
                )}
                <div>
                  <p className="text-[13px] mb-1" style={{ color: colors.secondaryText }}>Price chart with trade markers</p>
                  <TradingChart
                    symbol={results.symbol.replace('.NS', '')}
                    height={350}
                    showBuySell={false}
                    defaultTimeframe="1d"
                    backtestTrades={results.trades || []}
                    backtestConfig={results.config || {}}
                    showTradeMarkers={true}
                    highlightedTradeIndex={highlightedTradeIndex}
                    replayUpToDate={replayUpToDate}
                    onCandlesLoaded={setCandleDates}
                  />
                </div>
              </div>
            )}

            {/* Trade Log */}
            {(results.trades || []).length > 0 && (
              <div className="rounded-lg p-1.5" style={cs}>
                <p className="text-[13px] font-semibold mb-2" style={{ color: colors.secondaryText }}>Trade Log</p>
                <div className="space-y-1.5 max-h-64 overflow-y-auto">
                  {(results.trades || []).map((t, i) => (
                    <div key={i} className="py-1.5 border-b" style={{ borderColor: colors.border + '60' }}>
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-[13px] font-medium" style={{ color: colors.primaryText }}>
                            {t.entry_date} → {t.exit_date}
                          </span>
                          <span className="text-[13px] ml-2 px-1.5 py-0.5 rounded"
                            style={{ backgroundColor: `${t.exit_reason === 'SL' ? colors.loss : t.exit_reason === 'TP' ? colors.profit : t.exit_reason === 'EXIT_COND' ? '#ef4444' : colors.accent}20`, color: t.exit_reason === 'SL' ? colors.loss : t.exit_reason === 'TP' ? colors.profit : t.exit_reason === 'EXIT_COND' ? '#ef4444' : colors.accent }}>
                            {t.exit_reason === 'EXIT_COND' ? 'COND' : t.exit_reason}
                          </span>
                          {t.quantity && <span className="text-[12px] ml-1" style={{ color: colors.secondaryText }}>×{t.quantity}</span>}
                        </div>
                        <div className="text-right">
                          <p className="text-[13px] font-bold" style={{ color: (t.pnl || 0) >= 0 ? colors.profit : colors.loss }}>
                            {(t.pnl || 0) >= 0 ? '+' : ''}₹{Math.abs(t.pnl || 0).toFixed(0)}
                          </p>
                          <p className="text-[12px]" style={{ color: colors.secondaryText }}>
                            {(t.pnl_pct || 0) >= 0 ? '+' : ''}{(t.pnl_pct || 0).toFixed(2)}%
                          </p>
                        </div>
                      </div>
                      {(t.triggered_conditions || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {t.triggered_conditions.map(cid => {
                            const cond = availableConditions.find(c => c.id === cid);
                            return (
                              <span key={cid} className="px-1.5 py-0.5 rounded text-[10px] font-semibold"
                                style={{ backgroundColor: `${colors.accent}18`, color: colors.accent }}>
                                {cond ? cond.label : cid}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(results.num_trades === 0) && (
              <div className="rounded-lg p-1.5 text-center" style={cs}>
                <p className="text-[14px]" style={{ color: colors.secondaryText }}>No trades were triggered in this period.</p>
                <p className="text-[13px] mt-1" style={{ color: colors.secondaryText }}>Try relaxing entry conditions or using ANY logic.</p>
              </div>
            )}

            <button onClick={() => setView('config')}
              className="w-full py-1 rounded-lg text-[14px] font-medium"
              style={{ backgroundColor: colors.inputBg, color: colors.primaryText, borderWidth: '1px', borderColor: colors.border }}>
              ? Configure New Run
            </button>
          </div>
        )}

        {/* -- PORTFOLIO RESULTS VIEW -- */}
        {view === 'portfolio-results' && portfolioResults && (() => {
          const pr = portfolioResults;
          const returnPct = pr.portfolio_return_pct || 0;
          const returnColor = returnPct >= 0 ? colors.profit : colors.loss;
          return (
            <div className="space-y-3">
              {/* Summary card */}
              <div className="rounded-xl p-3" style={cs}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[13px] font-bold" style={{ color: colors.primaryText }}>Portfolio Results</p>
                  <span className="text-[12px] px-2 py-0.5 rounded-full font-semibold"
                    style={{ backgroundColor: `${returnColor}20`, color: returnColor }}>
                    {returnPct >= 0 ? '+' : ''}{returnPct.toFixed(2)}%
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    ['Capital', `?${Number(pr.total_capital).toLocaleString('en-IN')}`],
                    ['Final Value', `?${Number(pr.final_equity).toLocaleString('en-IN')}`],
                    ['Per Position', `?${Number(pr.per_position_capital).toLocaleString('en-IN')}`],
                    ['Total Trades', pr.total_trades],
                    ['Win Rate', pr.win_rate_pct != null ? `${Number(pr.win_rate_pct).toFixed(1)}%` : '…'],
                    ['Max Drawdown', pr.max_drawdown_pct != null ? `${Number(pr.max_drawdown_pct).toFixed(2)}%` : '…'],
                    ['Sharpe', pr.sharpe_ratio != null ? Number(pr.sharpe_ratio).toFixed(2) : '…'],
                    ['Profit Factor', pr.profit_factor != null ? Number(pr.profit_factor).toFixed(2) : '…'],
                  ].map(([label, val]) => (
                    <div key={label} className="rounded-lg px-2 py-1.5" style={{ backgroundColor: colors.inputBg }}>
                      <p className="text-[11px]" style={{ color: colors.secondaryText }}>{label}</p>
                      <p className="text-[13px] font-bold" style={{ color: colors.primaryText }}>{val}</p>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] mt-2" style={{ color: colors.secondaryText }}>
                  {pr.symbols.length} stocks — max {pr.max_positions} concurrent — {startDate} → {endDate}
                </p>
              </div>

              {/* Per-stock ranked table */}
              <div className="rounded-xl p-3" style={cs}>
                <p className="text-[11px] font-semibold uppercase tracking-widest mb-2" style={{ color: colors.secondaryText }}>
                  Per Stock Results <span className="normal-case font-normal">(tap to expand trades)</span>
                </p>
                <div className="space-y-1">
                  {/* Header */}
                  <div className="grid text-[10px] font-semibold px-2 pb-1" style={{ gridTemplateColumns: '3fr 2fr 1.5fr 1.5fr', color: colors.secondaryText }}>
                    <span>Symbol</span><span className="text-right">Return</span><span className="text-right">Trades</span><span className="text-right">Win %</span>
                  </div>
                  {(pr.per_stock_results || []).map(stock => {
                    const sColor = stock.total_return_pct >= 0 ? colors.profit : colors.loss;
                    const isExpanded = expandedStock === stock.symbol;
                    return (
                      <div key={stock.symbol}>
                        <button className="w-full rounded-lg px-2 py-2 transition-all"
                          style={{ backgroundColor: isExpanded ? `${colors.accent}10` : colors.inputBg, border: `1px solid ${isExpanded ? colors.accent : colors.border}30` }}
                          onClick={() => setExpandedStock(isExpanded ? null : stock.symbol)}>
                          <div className="grid items-center text-[13px]" style={{ gridTemplateColumns: '3fr 2fr 1.5fr 1.5fr' }}>
                            <span className="font-bold text-left" style={{ color: colors.primaryText }}>{stock.symbol}</span>
                            <span className="font-bold text-right" style={{ color: sColor }}>
                              {stock.total_return_pct >= 0 ? '+' : ''}{Number(stock.total_return_pct).toFixed(1)}%
                            </span>
                            <span className="text-right" style={{ color: colors.secondaryText }}>{stock.num_trades}</span>
                            <span className="text-right" style={{ color: colors.secondaryText }}>
                              {stock.win_rate_pct != null ? `${Number(stock.win_rate_pct).toFixed(0)}%` : '…'}
                            </span>
                          </div>
                          {stock.total_pnl !== 0 && (
                            <p className="text-[11px] text-left mt-0.5" style={{ color: sColor }}>
                              {stock.total_pnl >= 0 ? '+' : ''}₹{Math.abs(stock.total_pnl).toFixed(0)} P&L
                            </p>
                          )}
                        </button>
                        {/* Expanded trade log */}
                        {isExpanded && (stock.trades || []).length > 0 && (
                          <div className="ml-2 mt-1 space-y-1 pb-1">
                            {stock.trades.map((t, ti) => (
                              <div key={ti} className="rounded-lg px-2 py-1.5" style={{ backgroundColor: colors.inputBg }}>
                                <div className="flex justify-between items-center">
                                  <div>
                                    <span className="text-[12px]" style={{ color: colors.primaryText }}>{t.entry_date} → {t.exit_date}</span>
                                    <span className="ml-1.5 text-[11px] px-1.5 py-0.5 rounded"
                                      style={{ backgroundColor: `${t.exit_reason === 'SL' ? colors.loss : t.exit_reason === 'TP' ? colors.profit : colors.accent}20`, color: t.exit_reason === 'SL' ? colors.loss : t.exit_reason === 'TP' ? colors.profit : colors.accent }}>
                                      {t.exit_reason === 'EXIT_COND' ? 'COND' : t.exit_reason}
                                    </span>
                                  </div>
                                  <span className="text-[12px] font-bold" style={{ color: (t.pnl || 0) >= 0 ? colors.profit : colors.loss }}>
                                    {(t.pnl || 0) >= 0 ? '+' : ''}₹{Math.abs(t.pnl || 0).toFixed(0)}
                                  </span>
                                </div>
                                {(t.triggered_conditions || []).length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {t.triggered_conditions.map(cid => {
                                      const cond = availableConditions.find(c => c.id === cid);
                                      return <span key={cid} className="px-1.5 py-0.5 rounded text-[10px] font-semibold"
                                        style={{ backgroundColor: `${colors.accent}18`, color: colors.accent }}>{cond ? cond.label : cid}</span>;
                                    })}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        {isExpanded && (stock.trades || []).length === 0 && (
                          <p className="text-[12px] px-3 py-1.5" style={{ color: colors.secondaryText }}>No trades triggered for this stock.</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Yearly P&L Breakdown */}
              {(() => {
                const allTrades = pr.all_trades || [];
                if (allTrades.length < 2) return null;
                const yearMap = {};
                for (const t of allTrades) {
                  const yr = (t.entry_date || '').slice(0, 4) || 'Unknown';
                  if (!yearMap[yr]) yearMap[yr] = { trades: 0, wins: 0, pnl: 0, best: null, worst: null };
                  const m = yearMap[yr];
                  m.trades++;
                  if ((t.pnl || 0) > 0) m.wins++;
                  m.pnl += t.pnl || 0;
                  m.best  = m.best  == null ? (t.pnl || 0) : Math.max(m.best, t.pnl || 0);
                  m.worst = m.worst == null ? (t.pnl || 0) : Math.min(m.worst, t.pnl || 0);
                }
                const years = Object.keys(yearMap).sort();
                if (years.length < 1) return null;
                return (
                  <div className="rounded-lg p-1.5" style={cs}>
                    <p className="text-[13px] font-semibold mb-2" style={{ color: colors.secondaryText }}>Year-by-Year P&L (Portfolio)</p>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr style={{ borderBottom: `1px solid ${colors.border}60` }}>
                            {['Year','Trades','Wins','Win %','Net P&L','Best','Worst'].map(h => (
                              <th key={h} style={{ color: colors.secondaryText, fontWeight: 600, textAlign: h === 'Year' ? 'left' : 'right', padding: '4px 8px', whiteSpace: 'nowrap' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {years.map(yr => {
                            const m = yearMap[yr];
                            const winPct = m.trades > 0 ? Math.round((m.wins / m.trades) * 100) : 0;
                            const pnlColor = m.pnl >= 0 ? colors.profit : colors.loss;
                            return (
                              <tr key={yr} style={{ borderBottom: `1px solid ${colors.border}30` }}>
                                <td style={{ color: colors.primaryText, fontWeight: 700, padding: '5px 8px' }}>{yr}</td>
                                <td style={{ color: colors.primaryText, textAlign: 'right', padding: '5px 8px' }}>{m.trades}</td>
                                <td style={{ color: colors.secondaryText, textAlign: 'right', padding: '5px 8px' }}>{m.wins}</td>
                                <td style={{ color: winPct >= 50 ? colors.profit : colors.loss, textAlign: 'right', padding: '5px 8px', fontWeight: 600 }}>{winPct}%</td>
                                <td style={{ color: pnlColor, textAlign: 'right', padding: '5px 8px', fontWeight: 700 }}>{m.pnl >= 0 ? '+' : ''}₹{Math.abs(Math.round(m.pnl)).toLocaleString('en-IN')}</td>
                                <td style={{ color: colors.profit, textAlign: 'right', padding: '5px 8px' }}>+₹{Math.abs(Math.round(m.best || 0)).toLocaleString('en-IN')}</td>
                                <td style={{ color: colors.loss, textAlign: 'right', padding: '5px 8px' }}>-₹{Math.abs(Math.round(m.worst || 0)).toLocaleString('en-IN')}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}

              {/* Exit Outcomes */}
              {(() => {
                const allTrades = pr.all_trades || [];
                if (allTrades.length < 2) return null;
                const outcomeMap = {};
                for (const t of allTrades) {
                  const reason = t.exit_reason || 'OTHER';
                  if (!outcomeMap[reason]) outcomeMap[reason] = { count: 0, pnl: 0 };
                  outcomeMap[reason].count++;
                  outcomeMap[reason].pnl += t.pnl || 0;
                }
                const total = allTrades.length;
                const rows = Object.entries(outcomeMap).sort((a, b) => b[1].count - a[1].count);
                const labelMap = { TP: 'Target Hit', SL: 'Stop Loss', SIGNAL: 'Signal Exit', EXIT_COND: 'Cond Exit', END: 'End of Period' };
                const colorMap = { TP: colors.profit, SL: colors.loss, SIGNAL: colors.accent, EXIT_COND: '#ef4444', END: '#6b7280' };
                return (
                  <div className="rounded-lg p-1.5" style={cs}>
                    <p className="text-[13px] font-semibold mb-2" style={{ color: colors.secondaryText }}>Exit Outcomes Breakdown</p>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr style={{ borderBottom: `1px solid ${colors.border}60` }}>
                            {['Exit Type','Count','Share','Total P&L','Avg / Trade'].map(h => (
                              <th key={h} style={{ color: colors.secondaryText, fontWeight: 600, textAlign: h === 'Exit Type' ? 'left' : 'right', padding: '4px 8px', whiteSpace: 'nowrap' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map(([reason, m]) => {
                            const col = colorMap[reason] || '#6b7280';
                            const share = Math.round((m.count / total) * 100);
                            const avg = m.count > 0 ? m.pnl / m.count : 0;
                            return (
                              <tr key={reason} style={{ borderBottom: `1px solid ${colors.border}30` }}>
                                <td style={{ padding: '5px 8px' }}>
                                  <span style={{ background: `${col}18`, color: col, borderRadius: 6, padding: '2px 8px', fontWeight: 700, fontSize: 12 }}>{labelMap[reason] || reason}</span>
                                </td>
                                <td style={{ color: colors.primaryText, textAlign: 'right', padding: '5px 8px', fontWeight: 600 }}>{m.count}</td>
                                <td style={{ color: colors.secondaryText, textAlign: 'right', padding: '5px 8px' }}>{share}%</td>
                                <td style={{ color: m.pnl >= 0 ? colors.profit : colors.loss, textAlign: 'right', padding: '5px 8px', fontWeight: 700 }}>{m.pnl >= 0 ? '+' : ''}₹{Math.abs(Math.round(m.pnl)).toLocaleString('en-IN')}</td>
                                <td style={{ color: avg >= 0 ? colors.profit : colors.loss, textAlign: 'right', padding: '5px 8px' }}>{avg >= 0 ? '+' : ''}₹{Math.abs(Math.round(avg)).toLocaleString('en-IN')}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}

              {/* Monthly returns heatmap */}
              {renderMonthlyReturns(pr.monthly_returns)}

              <button onClick={() => setView('config')}
                className="w-full py-1.5 rounded-lg text-[14px] font-medium"
                style={{ backgroundColor: colors.inputBg, color: colors.primaryText, border: `1px solid ${colors.border}` }}>
                ? Configure New Run
              </button>
            </div>
          );
        })()}

        {/* -- FROM ALGO FLOW VIEW -- */}
        {view === 'from-strategy' && (
          <div className="space-y-3">
            {/* Strategy selector */}
            <div className="rounded-lg p-1.5 space-y-2" style={cs}>
              <p className="text-[13px] font-semibold" style={{ color: colors.secondaryText }}>Select Algo Flow Strategy</p>
              {algoStrategies.length === 0 ? (
                <p className="text-[13px]" style={{ color: colors.secondaryText }}>No strategies found. Create one in Algo Flows first.</p>
              ) : (
                <select className="w-full px-1.5 py-1 rounded text-[14px]" style={inputStyle}
                  value={fsStrategyId} onChange={e => { setFsStrategyId(e.target.value); setFsResult(null); setFsError(''); }}>
                  <option value=""> Choose a strategy …</option>
                  {algoStrategies.map(s => (
                    <option key={s.id} value={s.id}>{s.name} ({s.status})</option>
                  ))}
                </select>
              )}
            </div>

            {/* Conditions preview (read-only) */}
            {fsStrategyData && (
              <div className="rounded-lg p-1.5" style={cs}>
                <p className="text-[13px] font-semibold mb-1" style={{ color: colors.secondaryText }}>Strategy Config (read-only)</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-[13px] mb-2">
                  <div>
                    <span style={{ color: colors.secondaryText }}>Entry Logic</span>
                    <p className="font-medium" style={{ color: colors.primaryText }}>{fsStrategyData.config?.entry_logic || 'ALL'}</p>
                  </div>
                  <div>
                    <span style={{ color: colors.secondaryText }}>Stop Loss</span>
                    <p className="font-medium" style={{ color: colors.primaryText }}>{fsStrategyData.config?.stop_loss_pct ?? 5}%</p>
                  </div>
                  <div>
                    <span style={{ color: colors.secondaryText }}>Target</span>
                    <p className="font-medium" style={{ color: colors.primaryText }}>{fsStrategyData.config?.target_pct ?? 10}%</p>
                  </div>
                </div>
                {(fsStrategyData.conditions || []).length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {fsStrategyData.conditions.map(c => (
                      <span key={c.name} className="px-1.5 py-0.5 rounded text-[12px] font-medium"
                        style={{ backgroundColor: `${colors.accent}20`, color: colors.accent }}>
                        {c.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Symbol + Date + Capital */}
            {fsStrategyData && (
              <div className="rounded-lg p-1.5 space-y-2" style={cs}>
                <p className="text-[13px] font-semibold mb-1" style={{ color: colors.secondaryText }}>Backtest Parameters</p>
                <div>
                  <label className="text-[13px]" style={{ color: colors.secondaryText }}>Symbol</label>
                  <select className="w-full px-1.5 py-1 rounded text-[14px] mt-0.5" style={inputStyle}
                    value={fsSymbol} onChange={e => setFsSymbol(e.target.value)}>
                    <option value="">— Select stock —</option>
                    {(fsStrategyData.stocks || []).length > 0 && (
                      <optgroup label="Strategy stocks">
                        {fsStrategyData.stocks.map(s => <option key={s} value={s}>{s.replace('.NS', '')}</option>)}
                      </optgroup>
                    )}
                    {stocks.map(s => (
                      <option key={s.symbol} value={s.symbol}>{s.symbol.replace('.NS','')} — {s.company_name}</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <label className="text-[13px]" style={{ color: colors.secondaryText }}>Start Date</label>
                    <input type="date" className="w-full px-1.5 py-1 rounded text-[13px] mt-0.5" style={inputStyle}
                      value={fsStartDate} onChange={e => setFsStartDate(e.target.value)} />
                  </div>
                  <div>
                    <label className="text-[13px]" style={{ color: colors.secondaryText }}>End Date</label>
                    <input type="date" className="w-full px-1.5 py-1 rounded text-[13px] mt-0.5" style={inputStyle}
                      value={fsEndDate} onChange={e => setFsEndDate(e.target.value)} />
                  </div>
                  <div>
                    <label className="text-[13px]" style={{ color: colors.secondaryText }}>Capital (?)</label>
                    <input type="number" className="w-full px-1.5 py-1 rounded text-[13px] mt-0.5" style={inputStyle}
                      value={fsCapital} onChange={e => setFsCapital(e.target.value)} min={10000} step={10000} />
                  </div>
                  <div>
                    <label className="text-[13px]" style={{ color: colors.secondaryText }}>Qty per trade</label>
                    <input type="number" className="w-full px-1.5 py-1 rounded text-[13px] mt-0.5" style={inputStyle}
                      value={fsQty} onChange={e => setFsQty(e.target.value)} min={1} step={1} />
                  </div>
                </div>
              </div>
            )}

            {fsError && <p className="text-[13px] text-red-400">{fsError}</p>}

            {fsStrategyData && (
              <button onClick={handleRunFromStrategy} disabled={fsLoading}
                className="w-full py-1.5 rounded-lg text-[14px] font-semibold text-white"
                style={{ backgroundColor: fsLoading ? colors.secondaryText : colors.accent }}>
                {fsLoading ? 'Running…' : 'Run Backtest'}
              </button>
            )}

            {/* Results */}
            {fsResult && (
              <div className="rounded-lg p-1.5 space-y-2" style={cs}>
                <p className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>
                  Results — {fsResult.symbol?.replace('.NS', '')} — {fsResult.start_date} → {fsResult.end_date}
                </p>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 text-[13px]">
                  {[
                    ['Return', pct(fsResult.total_return_pct), (fsResult.total_return_pct || 0) >= 0 ? colors.profit : colors.loss],
                    ['Win Rate', pct(fsResult.win_rate_pct), colors.primaryText],
                    ['Trades', fsResult.num_trades, colors.primaryText],
                    ['Max DD', pct(fsResult.max_drawdown_pct), colors.loss],
                    ['Sharpe', fsResult.sharpe_ratio?.toFixed(2) ?? '…', colors.primaryText],
                    ['Final Capital', `?${fsResult.capital_final?.toLocaleString('en-IN')}`, colors.primaryText],
                  ].map(([label, val, color]) => (
                    <div key={label} className="rounded p-1" style={{ backgroundColor: colors.inputBg }}>
                      <p style={{ color: colors.secondaryText }}>{label}</p>
                      <p className="font-bold mt-0.5" style={{ color }}>{val}</p>
                    </div>
                  ))}
                </div>
                <p className="text-[13px]" style={{ color: colors.secondaryText }}>
                  Result saved to History.
                </p>
              </div>
            )}
          </div>
        )}

        {/* -- HISTORY VIEW -- */}
        {view === 'history' && (
          <div className="space-y-2">
            {history.length === 0 ? (
              <div className="rounded-lg p-1.5 text-center" style={cs}>
                <p className="text-[14px]" style={{ color: colors.secondaryText }}>No backtest runs yet.</p>
                <button onClick={() => setView('config')} className="mt-3 text-[13px] font-medium" style={{ color: colors.accent }}>
                  Run your first backtest ?
                </button>
              </div>
            ) : history.map(r => (
              <button key={r.id} onClick={() => handleLoadResult(r.id)}
                className="w-full rounded-lg p-1.5 text-left transition-colors" style={cs}>
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>{r.name}</p>
                    <p className="text-[13px]" style={{ color: colors.secondaryText }}>{r.symbol.replace('.NS','')} — {r.start_date} → {r.end_date}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[13px] font-bold" style={{ color: (r.total_return_pct || 0) >= 0 ? colors.profit : colors.loss }}>
                      {r.total_return_pct != null ? `${r.total_return_pct >= 0 ? '+' : ''}${parseFloat(r.total_return_pct).toFixed(2)}%` : '…'}
                    </p>
                    <p className="text-[13px]" style={{ color: colors.secondaryText }}>{r.num_trades} trades</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      <ShareToForumModal
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        attachments={results ? [{ attach_type: 'backtest', snapshot: results }] : []}
        defaultTitle={results ? `Backtest: ${results.name || results.symbol}${results.total_return_pct != null ? ` — ${parseFloat(results.total_return_pct) >= 0 ? '+' : ''}${parseFloat(results.total_return_pct).toFixed(1)}% return` : ''}` : ''}
        defaultLifecycle="backtest"
      />

      {/* Hidden card for html2canvas */}
      {results && (
        <div style={{ position: 'fixed', left: -9999, top: 0, width: 1080, height: 1350, opacity: 0, pointerEvents: 'none', zIndex: -1 }}>
          <BacktestShareCard results={results} cardRef={shareCardRef} />
        </div>
      )}

      {/* Social share modal */}
      {socialOpen && results && (
        <div className="fixed inset-0 z-50 flex items-end justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }} onClick={() => setSocialOpen(false)}>
          <div className="w-full max-w-sm rounded-t-2xl p-5 space-y-4" style={{ background: colors.cardBg, border: `1px solid ${colors.border}` }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <p className="text-[15px] font-bold" style={{ color: colors.primaryText }}>Share Backtest Card</p>
              <button onClick={() => setSocialOpen(false)}><X className="w-4 h-4" style={{ color: colors.secondaryText }} /></button>
            </div>
            <div style={{ width: '100%', height: Math.round(1350 * (300/1080)), overflow: 'hidden', borderRadius: 10 }}>
              <div style={{ width: 1080, height: 1350, transform: `scale(${300/1080})`, transformOrigin: 'top left' }}>
                <BacktestShareCard results={results} />
              </div>
            </div>
            <p className="text-[12px] text-center" style={{ color: colors.secondaryText }}>Opens share sheet — pick Instagram, WhatsApp, or save to camera roll.</p>
            <button
              onClick={async () => { if (!shareCardRef.current) return; setCapturing(true); try { await captureAndShare(shareCardRef.current, 'backtest.png'); } finally { setCapturing(false); setSocialOpen(false); } }}
              disabled={capturing}
              className="w-full py-3 rounded-xl text-[14px] font-semibold flex items-center justify-center gap-2"
              style={{ background: colors.accent, color: '#fff', opacity: capturing ? 0.6 : 1 }}>
              {capturing ? <><Loader className="w-4 h-4 animate-spin" /> Preparing…</> : <><Share2 className="w-4 h-4" /> Share as Image</>}
            </button>
          </div>
        </div>
      )}
      </div>
    );
}
